import { Row } from '../schema/document'
import { PageElement } from '../schema/elements'
import { RichTextDoc } from '../schema/richText'
import { SectionKey, SiteSettings } from '../schema/site'

/**
 * What every template is built from: the ids a section is found by, the words
 * a page carries in each language, and the small constructors the section
 * builders share. A template itself is data (`registry.ts`); this is the code
 * under all three.
 */

/**
 * The row each section is found by, on the page and in the preview.
 *
 * Fixed rather than generated, so a section can be found by what it is rather
 * than by where it sits: the settings form scrolls to a section's card when
 * the row is clicked, and that only works because the id survives a rebuild.
 * The reservation section is two rows and the banner hero may be two; their
 * second rows carry this id with a suffix, which `sectionOfRowId` knows.
 */
export const ROW_ID: Record<SectionKey, string> = {
  hero: 'tpl-row-hero',
  menu: 'tpl-row-menu',
  about: 'tpl-row-about',
  visit: 'tpl-row-visit',
  gallery: 'tpl-row-gallery',
  reservations: 'tpl-row-room',
  map: 'tpl-row-map',
  review: 'tpl-row-review'
}

const EXTRA_ROW_PREFIX = 'tpl-row-extra-'

export const rowIdOfExtra = (id: string) => `${EXTRA_ROW_PREFIX}${id}`

/** The block an extra's row belongs to, or nothing for a section's row. */
export const extraOfRowId = (rowId: string): string | undefined =>
  rowId.startsWith(EXTRA_ROW_PREFIX)
    ? rowId.slice(EXTRA_ROW_PREFIX.length)
    : undefined

/** The section a row belongs to, or nothing for an extra's row. */
export function sectionOfRowId(rowId: string): SectionKey | undefined {
  if (extraOfRowId(rowId) !== undefined) return undefined
  return (Object.keys(ROW_ID) as SectionKey[]).find(
    key => rowId === ROW_ID[key] || rowId.startsWith(`${ROW_ID[key]}-`)
  )
}

/*
  Section headings the templates write into the page. Content, not interface,
  so it lives here rather than in the i18next catalogue: a page is built in
  every language at once, and `t()` only ever knows the one the panel is in.
*/
const COPY: Record<string, Record<string, string>> = {
  pl: {
    menu: 'Menu',
    about: 'O nas',
    hours: 'Godziny otwarcia',
    contact: 'Kontakt',
    gallery: 'Galeria',
    call: 'Zadzwoń',
    write: 'Napisz do nas',
    reservations: 'Rezerwacje',
    roomNote:
      'Tak wygląda u nas w środku. Wybierz stolik na planie i zarezerwuj go poniżej.',
    consent:
      'Wyrażam zgodę na przetwarzanie moich danych przez {name} w celu obsługi rezerwacji.',
    consentNoName:
      'Wyrażam zgodę na przetwarzanie moich danych przez lokal w celu obsługi rezerwacji.',
    book: 'Zarezerwuj stolik',
    closed: 'zamknięte',
    review: 'Zostaw opinię w Google'
  },
  en: {
    menu: 'Menu',
    about: 'About us',
    hours: 'Opening hours',
    contact: 'Contact',
    gallery: 'Gallery',
    call: 'Call us',
    write: 'Email us',
    reservations: 'Reservations',
    roomNote:
      'This is what it looks like inside. Pick a table on the plan and book it below.',
    consent:
      'I agree to {name} processing my details in order to handle my booking.',
    consentNoName:
      'I agree to the venue processing my details in order to handle my booking.',
    book: 'Book a table',
    closed: 'closed',
    review: 'Leave a Google review'
  }
}

export const copyFor = (language: string, key: string) =>
  COPY[language]?.[key] ?? COPY.en[key]

/** The same string in every language the site has. */
export const everywhere = (languages: string[], value: string) =>
  Object.fromEntries(languages.map(language => [language, value]))

/**
 * What a section is called on the finished page, in one language.
 *
 * The settings form uses it to name a section's card after the heading the
 * visitor will see rather than after a word only this code knows.
 */
export function sectionHeading(language: string, key: string): string {
  return COPY[language]?.[key] ?? COPY.en[key] ?? key
}

/** A section heading, translated where the template knows the language. */
export const headingCopy = (languages: string[], key: string) =>
  Object.fromEntries(
    languages.map(language => [language, copyFor(language, key)])
  )

export const paragraph = (text: string): RichTextDoc => ({
  type: 'doc',
  content: text.split('\n').map(line =>
    line.trim()
      ? {
          type: 'paragraph' as const,
          content: [{ type: 'text' as const, text: line }]
        }
      : { type: 'paragraph' as const }
  )
})

export const localizedParagraphs = (
  languages: string[],
  value: Record<string, string | undefined>
) =>
  Object.fromEntries(
    languages.map(language => [language, paragraph(value[language] ?? '')])
  )

export const formatAddress = (settings: SiteSettings) =>
  [
    settings.street,
    [settings.postalCode, settings.city].filter(Boolean).join(' ')
  ]
    .filter(Boolean)
    .join(', ')

export const row = (
  id: string,
  layout: Row['layout'],
  columns: Row['columns'],
  extra: Partial<Row> = {}
): Row => ({
  id,
  layout,
  background: { type: 'none' },
  paddingY: 'M',
  maxWidth: 'contained',
  verticalAlign: 'top',
  gap: 'M',
  mobileBehavior: 'stack',
  hidden: false,
  columns,
  ...extra
})

export const heading = (
  id: string,
  text: Record<string, string>,
  align: 'left' | 'center' | 'right' = 'left'
): PageElement => ({ id, type: 'heading', level: 2, text, align })

/** A photo as the page draws one: cropped to its shape, corners rounded. */
export const imageElement = (
  id: string,
  assetId: string,
  alt: Record<string, string>,
  ratio: '4:3' | '16:9'
): PageElement => ({
  id,
  type: 'image',
  assetId,
  alt,
  ratio,
  fit: 'cover',
  focalPoint: { x: 0.5, y: 0.5 },
  rounded: true
})
